import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  contentDefinitions,
  getDefaultValueForLocale,
  supportedLocales,
  type ContentDefinition,
} from '../../config/contentDefinitions';
import {
  deleteContentEntry,
  saveContentEntry,
  subscribeToContentEntries,
} from '../../services/content';
import type { ContentEntry } from '../../types/content';

type Locale = (typeof supportedLocales)[number];

type EntryMap = Record<string, ContentEntry>;
type DraftMap = Record<string, string>;

type FeedbackState = {
  variant: 'success' | 'error';
  message: string;
} | null;

const formatValueForInput = (definition: ContentDefinition, value: string | string[] | undefined): string => {
  if (definition.type === 'list') {
    if (!value) {
      return '';
    }
    const list = Array.isArray(value) ? value : String(value).split('\n');
    return list.join('\n');
  }

  if (Array.isArray(value)) {
    return value.join('\n');
  }

  return value ?? '';
};

const parseInputValue = (definition: ContentDefinition, rawValue: string): string | string[] => {
  if (definition.type === 'list') {
    return rawValue
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }
  return rawValue;
};

const groupDefinitions = () => {
  const groups = new Map<string, ContentDefinition[]>();
  contentDefinitions.forEach((definition) => {
    const existing = groups.get(definition.section) ?? [];
    existing.push(definition);
    groups.set(definition.section, existing);
  });
  return Array.from(groups.entries()).map(([section, definitions]) => ({ section, definitions }));
};

export const ContentManager = () => {
  const [locale, setLocale] = useState<Locale>('it');
  const [entries, setEntries] = useState<EntryMap>({});
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToContentEntries(
      locale,
      (items) => {
        const map: EntryMap = {};
        items.forEach((entry) => {
          map[entry.contentKey] = entry;
        });
        setEntries(map);
        setDrafts({});
      },
      (error) => {
        setFeedback({ variant: 'error', message: error.message });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [locale]);

  useEffect(() => {
    if (!feedback) {
      return;
    }
    const timeout = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const definitionGroups = useMemo(groupDefinitions, []);

  const handleInputChange = (contentKey: string, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [contentKey]: value,
    }));
  };

  const getDisplayValue = (definition: ContentDefinition) => {
    const entry = entries[definition.contentKey];
    const defaultValue = getDefaultValueForLocale(definition, locale);
    const baseValue = entry?.value ?? defaultValue;
    if (Object.prototype.hasOwnProperty.call(drafts, definition.contentKey)) {
      return drafts[definition.contentKey];
    }
    return formatValueForInput(definition, baseValue);
  };

  const hasDraftChanged = (definition: ContentDefinition) => {
    if (!Object.prototype.hasOwnProperty.call(drafts, definition.contentKey)) {
      return false;
    }
    const entry = entries[definition.contentKey];
    const defaultValue = getDefaultValueForLocale(definition, locale);
    const baseValue = entry?.value ?? defaultValue;
    const formattedBase = formatValueForInput(definition, baseValue);
    return drafts[definition.contentKey] !== formattedBase;
  };

  const isCustomized = (definition: ContentDefinition) => entries[definition.contentKey] !== undefined;

  const getValueForExport = (definition: ContentDefinition): string | string[] => {
    const entry = entries[definition.contentKey];
    const storedValue = entry?.value ?? getDefaultValueForLocale(definition, locale);

    if (definition.type === 'list') {
      if (Array.isArray(storedValue)) {
        return storedValue.map((item) => String(item).trim()).filter((item) => item.length > 0);
      }
      if (typeof storedValue === 'string') {
        return storedValue
          .split('\n')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
      return [];
    }

    if (Array.isArray(storedValue)) {
      return storedValue.join(' ');
    }

    return (storedValue as string | undefined) ?? '';
  };

  const handleExport = () => {
    const payload: Record<string, string | string[]> = {};

    contentDefinitions.forEach((definition) => {
      payload[definition.contentKey] = getValueForExport(definition);
    });

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `content-${locale}-${timestamp}.json`;
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setBulkPending(true);
      const text = await file.text();
      const data = JSON.parse(text) as Record<string, unknown>;

      if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        throw new Error('Formato JSON non valido. Fornire un oggetto chiave-valore.');
      }

      let updatedCount = 0;

      for (const definition of contentDefinitions) {
        if (!Object.prototype.hasOwnProperty.call(data, definition.contentKey)) {
          continue;
        }

        const rawValue = (data as Record<string, unknown>)[definition.contentKey];
        let value: string | string[];

        if (definition.type === 'list') {
          if (Array.isArray(rawValue)) {
            value = rawValue.map((item) => String(item).trim()).filter((item) => item.length > 0);
          } else if (typeof rawValue === 'string') {
            value = rawValue
              .split('\n')
              .map((item) => item.trim())
              .filter((item) => item.length > 0);
          } else {
            console.warn(`Valore ignorato per ${definition.contentKey}: formato non supportato.`);
            continue;
          }
        } else {
          if (typeof rawValue === 'string') {
            value = rawValue;
          } else if (rawValue === null || rawValue === undefined) {
            value = '';
          } else {
            value = String(rawValue);
          }
        }

        await saveContentEntry({
          contentKey: definition.contentKey,
          locale,
          section: definition.section,
          label: definition.label,
          helper: definition.helper,
          type: definition.type,
          value,
        });

        updatedCount += 1;
      }

      setFeedback({
        variant: 'success',
        message:
          updatedCount > 0
            ? `Import completato: ${updatedCount} elementi aggiornati per ${locale.toUpperCase()}.`
            : 'Import completato: nessun campo corrispondente nel file.',
      });
    } catch (error) {
      console.error(error);
      setFeedback({ variant: 'error', message: 'Errore durante l\'import del file JSON.' });
    } finally {
      setBulkPending(false);
      event.target.value = '';
    }
  };

  const handleSave = async (definition: ContentDefinition) => {
    const key = definition.contentKey;
    const hasDraft = Object.prototype.hasOwnProperty.call(drafts, key);
    const raw = hasDraft ? drafts[key] : getDisplayValue(definition);

    try {
      setPendingKey(key);
      await saveContentEntry({
        contentKey: key,
        locale,
        section: definition.section,
        label: definition.label,
        helper: definition.helper,
        type: definition.type,
        value: parseInputValue(definition, raw),
      });
      setFeedback({ variant: 'success', message: `Contenuto "${definition.label}" salvato.` });
      setDrafts((prev) => {
        const { [key]: _discarded, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error(error);
      setFeedback({ variant: 'error', message: 'Errore durante il salvataggio del contenuto.' });
    } finally {
      setPendingKey(null);
    }
  };

  const handleReset = async (definition: ContentDefinition) => {
    const key = definition.contentKey;
    const confirmation = window.confirm(
      'Ripristinare il contenuto originale? Il testo tornerà alla versione predefinita.',
    );
    if (!confirmation) {
      return;
    }

    try {
      setPendingKey(key);
      await deleteContentEntry(locale, key);
      setFeedback({ variant: 'success', message: `Contenuto "${definition.label}" ripristinato.` });
      setDrafts((prev) => {
        const { [key]: _discarded, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error(error);
      setFeedback({ variant: 'error', message: 'Errore durante il ripristino del contenuto.' });
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-50">Gestione contenuti testuali</h2>
          <p className="text-sm text-slate-400">
            Ogni modifica è salvata nel CMS e sostituisce il testo tradotto predefinito. Usa un elemento per riga nei campi elenco.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Lingua</label>
            <select
              className="w-40 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 focus:border-indigo-500 focus:outline-none"
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
              disabled={bulkPending}
            >
              {supportedLocales.map((supportedLocale) => (
                <option key={supportedLocale} value={supportedLocale}>
                  {supportedLocale.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-indigo-400 hover:text-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleExport}
              disabled={bulkPending}
            >
              Esporta JSON {locale.toUpperCase()}
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleImportClick}
              disabled={bulkPending}
            >
              Importa JSON {locale.toUpperCase()}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={handleImport}
          />
        </div>
      </header>

      {feedback && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            feedback.variant === 'success'
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
              : 'border-red-500/40 bg-red-500/10 text-red-200'
          }`}
        >
          {feedback.message}
        </div>
      )}

      <div className="space-y-10">
        {definitionGroups.map(({ section, definitions }) => (
          <section key={section} className="space-y-4">
            <header>
              <h3 className="text-lg font-semibold text-slate-100">{section}</h3>
            </header>
            <div className="grid gap-4 lg:grid-cols-2">
              {definitions.map((definition) => {
                const value = getDisplayValue(definition);
                const dirty = hasDraftChanged(definition);
                const customized = isCustomized(definition);
                const entry = entries[definition.contentKey];

                return (
                  <article
                    key={definition.contentKey}
                    className="flex h-full flex-col gap-4 rounded-3xl border border-slate-800 bg-slate-900/50 p-5 shadow-lg"
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-base font-semibold text-slate-50">{definition.label}</h4>
                          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
                            {definition.contentKey}
                          </p>
                        </div>
                        {customized && (
                          <span className="rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-semibold text-indigo-200">
                            Personalizzato
                          </span>
                        )}
                      </div>
                      {definition.helper && <p className="text-sm text-slate-300">{definition.helper}</p>}
                      {entry?.updatedAt && (
                        <p className="text-xs text-slate-500">
                          Ultimo aggiornamento: {entry.updatedAt.toLocaleString()}
                        </p>
                      )}
                    </div>

                    {definition.type === 'text' ? (
                      <input
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 focus:border-indigo-500 focus:outline-none"
                        value={value}
                        onChange={(event) => handleInputChange(definition.contentKey, event.target.value)}
                      />
                    ) : (
                      <textarea
                        className="min-h-[140px] w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 focus:border-indigo-500 focus:outline-none"
                        value={value}
                        onChange={(event) => handleInputChange(definition.contentKey, event.target.value)}
                      />
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleSave(definition)}
                        disabled={pendingKey === definition.contentKey || (!dirty && !customized)}
                      >
                        {pendingKey === definition.contentKey ? 'Salvataggio…' : 'Salva'}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-red-400 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleReset(definition)}
                        disabled={pendingKey === definition.contentKey || !customized}
                      >
                        Ripristina originale
                      </button>
                      {definition.type === 'list' && (
                        <span className="text-xs text-slate-500">Un elemento per riga.</span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
